/**
 * Multihub Tour Scheduler Block
 *
 * Multi-step tour scheduling wizard with pluggable data sources (Yardi/RentCafe, Entrata, etc.).
 * Fetches property credentials via the ApartmentSync (Repli360) API,
 * retrieves available appointment slots, and submits leads through
 * the Multihub lead capture system (window.__mh_leads__).
 *
 * Opt-in (`use_leads_tour_times`): skip ApartmentSync and load slots from
 * GET /tour-times instead. Lead submission still goes through __mh_leads__.
 *
 * Requires:
 *   - apartment-sync block active on the site (loads RRAC script / provides site_id)
 *     — not required when use_leads_tour_times is enabled
 *   - multihub-lead-capture injection block (provides window.__mh_leads__)
 *
 * Ported from widgets/src/multihub-yardi-tour-scheduler (HTML/CSS/jQuery).
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema, ComponentDesignSchema } from '@vibesites/runtime';
import { useBlockToken, EditableBlockToken, useProperty, useVibesiteEvents } from '@vibesites/runtime/react';
import { Event } from '@vibesites/runtime';
import { cn } from '@vibesites/ui-primitives';
import { createSource } from './sources';
import { isoToApiDate } from './sources/date-utils';
import { useTourSchedulerData } from './useTourSchedulerData';
import { useLeadsTourTimes } from './useLeadsTourTimes';
import { validateEmail, validatePhone } from '../../shared/form-validation';
import { PhoneInput } from '../../shared/PhoneInput';

// ============================================================================
// Constants
// ============================================================================

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December', 
];

const BREAKPOINTS = { sm: 0, md: 400, lg: 550, xl: 690 } as const;
type ContainerSize = 'sm' | 'md' | 'lg' | 'xl';

// ============================================================================
// Tour Type Definitions
// ============================================================================

interface TourTypeDef {
  apiTourType: string;
  heading: string;
  description: string;
}

const TOUR_TYPE_DEFINITIONS: Record<string, TourTypeDef> = {
  guided_tour: {
    apiTourType: 'Guided Tour',
    heading: 'Guided Tour',
    description: 'Tour the property with a leasing agent',
  },
  self_guided_tour: {
    apiTourType: 'Self-Guided Tour',
    heading: 'Self-Guided Tour',
    description: 'Explore the property at your own pace',
  },
  live_video_tour: {
    apiTourType: 'Live Video Tour',
    heading: 'Live Video Tour',
    description: 'Video call with a leasing agent',
  },
  virtual_tour: {
    apiTourType: 'Virtual Tour',
    heading: 'Virtual Tour',
    description: 'Take an interactive virtual tour online',
  },
};

// ============================================================================
// Types
// ============================================================================

type WizardStep = 'loading' | 'tour-type' | 'date-time' | 'form' | 'success';

interface TourOption {
  key: string;
  heading: string;
  description: string;
  link?: { href: string; target: string };
}

// Window.__mh_leads__ is declared in multihub-lead-form block

// ============================================================================
// Date Helpers
// ============================================================================

function formatDisplayDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  return `${MONTH_NAMES[parseInt(month, 10) - 1]} ${parseInt(day, 10)}, ${year}`;
}

function firstAvailableIso(dates: string[]): string | null {
  if (dates.length === 0) return null;
  const today = new Date();
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  for (let d = 0; d < 60; d++) {
    const checkDate = new Date(todayMidnight.getTime() + d * 86400000);
    const iso = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
    if (dates.includes(isoToApiDate(iso))) return iso;
  }
  return null;
}

// ============================================================================
// Bedroom Helpers
// ============================================================================

interface BedroomOption {
  label: string;
  value: string;
}

const BEDROOM_LABELS: Record<number, string> = {
  0: 'Studio', 1: 'One Bedroom', 2: 'Two Bedroom', 3: 'Three Bedroom',
  4: 'Four Bedroom', 5: 'Five Bedroom', 6: 'Six Bedroom', 7: 'Seven Bedroom or more',
};

const DEFAULT_BEDROOMS: BedroomOption[] = [
  { label: 'Any', value: 'any' },
  { label: 'Studio', value: '0' },
  { label: 'One Bedroom', value: '1' },
  { label: 'Two Bedroom', value: '2' },
  { label: 'Three Bedroom', value: '3' },
  { label: 'Four Bedroom or more', value: 'four_more' },
];

function bedroomOptionsFromNumbers(nums: number[]): BedroomOption[] {
  const opts: BedroomOption[] = [{ label: 'Any', value: 'any' }];
  for (const n of nums) {
    const label = BEDROOM_LABELS[n];
    if (label) opts.push({ label, value: String(n) });
  }
  return opts;
}

function parseBedroomsRange(rangeText: string | null | undefined): BedroomOption[] {
  if (!rangeText || typeof rangeText !== 'string') return DEFAULT_BEDROOMS;
  const trimmed = rangeText.trim();
  if (/^\d+$/.test(trimmed)) {
    const n = parseInt(trimmed, 10);
    if (n >= 0 && n <= 10) return bedroomOptionsFromNumbers([n]);
  }
  const match = trimmed.match(/^(\d+)\s*-\s*(\d+)$/);
  if (match) {
    const start = parseInt(match[1], 10);
    const end = parseInt(match[2], 10);
    if (start >= 0 && end >= start && end <= 10) {
      const range: number[] = [];
      for (let i = start; i <= end; i++) range.push(i);
      return bedroomOptionsFromNumbers(range);
    }
  }
  return DEFAULT_BEDROOMS;
}

function bedroomOptionsFromFloorplans(
  floorplans: Array<{ floorplan_beds_min?: string | number | null }> | null | undefined,
): BedroomOption[] | null {
  if (!floorplans?.length) return null;
  const beds = new Set<number>();
  for (const fp of floorplans) {
    const raw = String(fp.floorplan_beds_min ?? '').toUpperCase();
    const n = raw === 'S' ? 0 : parseInt(raw, 10);
    if (!isNaN(n)) beds.add(n);
  }
  if (beds.size === 0) return null;
  return bedroomOptionsFromNumbers([...beds].sort((a, b) => a - b));
}

// ============================================================================
// Tour Type Icons (SVG paths)
// ============================================================================

function TourTypeIcon({ tourKey }: { tourKey: string }) {
  const icons: Record<string, React.ReactNode> = {
    guided_tour: (
      <>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </>
    ),
    self_guided_tour: (
      <>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </>
    ),
    live_video_tour: (
      <>
        <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
        <rect x="2" y="6" width="14" height="12" rx="2" />
      </>
    ),
    virtual_tour: (
      <>
        <rect x="3" y="5" width="18" height="12" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </>
    ),
  };

  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[tourKey] || (
        <>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </>
      )}
    </svg>
  );
}

// ============================================================================
// Injected CSS (animations only — layout handled by Tailwind)
// ============================================================================

const INJECTED_CSS = `
@keyframes mh-tour-spin { to { transform: rotate(360deg); } }
@keyframes mh-tour-circle-pop { to { transform: scale(1); opacity: 1; } }
@keyframes mh-tour-check-draw { to { stroke-dashoffset: 0; } }
.mh-tour-success .mh-circle {
  fill: var(--vibe-color-primary, #28c76f);
  transform-origin: 60px 60px;
  transform: scale(0.85);
  opacity: 0;
  animation: mh-tour-circle-pop 0.65s ease-out forwards;
}
.mh-tour-success .mh-check {
  fill: none;
  stroke: var(--vibe-color-background, white);
  stroke-width: 10;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 120;
  stroke-dashoffset: 120;
  animation: mh-tour-check-draw 0.7s ease-out 0.35s forwards;
}
@media (prefers-reduced-motion: reduce) {
  .mh-tour-success .mh-circle,
  .mh-tour-success .mh-check {
    animation: none; opacity: 1; transform: none; stroke-dashoffset: 0;
  }
}
input[name="move_in_date"] {
  display: block;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: textfield;
}
input[name="move_in_date"]::-webkit-calendar-picker-indicator { display: none; -webkit-appearance: none; }
input[name="move_in_date"]::-webkit-inner-spin-button { display: none; }
input[name="move_in_date"]::-webkit-clear-button { display: none; }
input[name="move_in_date"]::-webkit-datetime-edit,
input[name="move_in_date"]::-webkit-datetime-edit-fields-wrapper,
input[name="move_in_date"]::-webkit-datetime-edit-text,
input[name="move_in_date"]::-webkit-datetime-edit-month-field,
input[name="move_in_date"]::-webkit-datetime-edit-day-field,
input[name="move_in_date"]::-webkit-datetime-edit-year-field {
  padding: 0;
}
input[name="move_in_date"]::-webkit-date-and-time-value {
  text-align: left;
  margin: 0;
  padding: 0;
  min-width: 0;
}
`;

// ============================================================================
// Sub-Components
// ============================================================================

function LoadingView() {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 gap-4">
      <div
        className="w-10 h-10 border-[3px] border-border border-t-primary rounded-full"
        style={{ animation: 'mh-tour-spin 0.8s linear infinite' }}
      />
      <p className="text-sm text-muted-foreground m-0">Loading tour options...</p>
    </div>
  );
}

function ContactUsView({ contactUrl }: { contactUrl: string }) {
  return (
    <div className="flex flex-col items-center gap-4 py-8 px-4 text-center">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      <h3 className="text-lg font-semibold text-foreground m-0">We're Here to Help</h3>
      <p className="text-sm text-muted-foreground m-0 max-w-xs">
        Online scheduling is currently unavailable. Please contact us directly to schedule a tour.
      </p>
      <a
        href={contactUrl}
        className="inline-block px-8 py-3 bg-primary text-primary-foreground rounded-md font-semibold text-[0.9375rem] no-underline"
      >
        Contact Us
      </a>
    </div>
  );
}

function SuccessView({ message }: { message: string }) {
  return (
    <div
      className="mh-tour-success absolute inset-0 z-10 bg-background flex flex-col justify-center items-center transition-opacity duration-[600ms]"
      role="status"
      aria-live="polite"
    >
      <svg width="120" height="120" viewBox="0 0 120 120" aria-hidden="true" focusable="false">
        <circle className="mh-circle" cx="60" cy="60" r="54" />
        <path className="mh-check" d="M36 62 L54 80 L86 44" />
      </svg>
      <p className="text-lg text-gray-700 text-center mt-4">{message}</p>
    </div>
  );
}

// ============================================================================
// Calendar Component
// ============================================================================

function Calendar({
  currentMonth,
  onMonthChange,
  selectedDate,
  onDateSelect,
  availableDates,
  isLoading,
}: {
  currentMonth: Date;
  onMonthChange: (delta: number) => void;
  selectedDate: string | null;
  onDateSelect: (iso: string) => void;
  availableDates: string[];
  isLoading?: boolean;
}) {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const availableDateSet = useMemo(() => new Set(availableDates), [availableDates]);

  const isPrevDisabled = year < today.getFullYear() || (year === today.getFullYear() && month <= today.getMonth());

  const days: React.ReactNode[] = [];

  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(
      <div key={`empty-${i}`} className="aspect-square flex items-center justify-center text-sm rounded-full cursor-default" />,
    );
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const thisDate = new Date(year, month, day);
    const isPast = thisDate < todayMidnight;
    const isToday = year === today.getFullYear() && month === today.getMonth() && day === today.getDate();
    const apiDate = isoToApiDate(iso);
    const hasSlots = availableDateSet.has(apiDate);
    const isSelected = iso === selectedDate;
    const isDisabled = isPast || !hasSlots;

    days.push(
      <button
        key={day}
        type="button"
        className={cn(
          'aspect-square flex items-center justify-center text-sm rounded-full border-none bg-transparent font-body text-foreground transition-colors duration-150 p-0',
          isDisabled && 'text-muted-foreground cursor-not-allowed',
          !isDisabled && 'cursor-pointer',
          isToday && !isSelected && 'border-2 border-primary',
          hasSlots && !isPast && !isSelected && 'font-bold',
          !isDisabled && !isSelected && 'hover:bg-secondary hover:text-secondary-foreground',
          isSelected && 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground font-bold',
        )}
        disabled={isDisabled}
        onClick={() => !isDisabled && onDateSelect(iso)}
        aria-label={`${MONTH_NAMES[month]} ${day}${hasSlots ? ' - slots available' : ''}`}
        aria-pressed={isSelected}
      >
        {day}
      </button>,
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          className={cn(
            'bg-transparent border-none p-1 text-foreground rounded flex items-center justify-center',
            isPrevDisabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer',
          )}
          onClick={() => !isPrevDisabled && onMonthChange(-1)}
          disabled={isPrevDisabled}
          aria-label="Previous month"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span className="text-base font-semibold text-foreground m-0">
          {MONTH_NAMES[month]} {year}
        </span>
        <button
          type="button"
          className="bg-transparent border-none cursor-pointer p-1 text-foreground rounded flex items-center justify-center"
          onClick={() => onMonthChange(1)}
          aria-label="Next month"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
      <div className="grid grid-cols-7 text-center text-xs font-semibold text-muted-foreground mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      {isLoading ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16">
          <div
            className="w-6 h-6 border-2 border-border border-t-primary rounded-full"
            style={{ animation: 'mh-tour-spin 0.8s linear infinite' }}
          />
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-0.5">{days}</div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function MultihubTourSchedulerComponent({ sectionId }: BlockProps) {
  // Runtime event bus — emits tour_scheduled on success, and
  // self_guided_tour_clicked when the user picks a self-guided link.
  const { emit } = useVibesiteEvents();

  // Content tokens
  const headerText = useBlockToken('header_text', { defaultValue: 'Schedule a Tour' });
  const successMessage = useBlockToken('success_message', { defaultValue: 'Thank you! Your tour has been scheduled.' });
  const errorMessage = useBlockToken('error_message', { defaultValue: 'Something went wrong. Please try again.' });
  const submitButtonText = useBlockToken('submit_button_text', { defaultValue: 'Schedule Tour' });
  const contactUsUrl = useBlockToken('contact_us_url', { defaultValue: '/contact-us' });
  const disableGuidedTour = useBlockToken('disable_guided_tour', { defaultValue: 'false' });
  const disableSelfGuidedTour = useBlockToken('disable_self_guided_tour', { defaultValue: 'false' });
  const disableLiveVideoTour = useBlockToken('disable_live_video_tour', { defaultValue: 'false' });
  const selfGuidedTourLink = useBlockToken('self_guided_tour_link', { defaultValue: '' });
  const virtualTourLink = useBlockToken('virtual_tour_link', { defaultValue: '' });
  const thankYouUrl = useBlockToken('thank_you_url', { defaultValue: '' });
  const bedroomListType = useBlockToken('bedroom_list_type', { defaultValue: 'derived' });
  const property = useProperty();
  const phoneRequiredToken = useBlockToken('phone_required', { defaultValue: 'true' });
  const phoneRequired = phoneRequiredToken !== 'false';

  const javascriptIdToken = useBlockToken('javascript_id', {
    defaultField: 'custom_apartmentsync_javascript_id',
  });

  const javascriptId = javascriptIdToken;

  // Off by default: ApartmentSync adapters. On: GET /tour-times (PMS-agnostic).
  const useLeadsTourTimesEnabled = useBlockToken('use_leads_tour_times', { defaultValue: 'false' }) === 'true';

  // Data source
  const sourceToken = useBlockToken('source', { defaultValue: 'yardi' });
  const sourceType = (sourceToken || 'yardi').toLowerCase();
  const source = useMemo(() => createSource(sourceType), [sourceType]);

  // UI state — availability comes from useTourSchedulerData or useLeadsTourTimes
  const [step, setStep] = useState<WizardStep>('loading');
  const [selectedTourType, setSelectedTourType] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [containerSize, setContainerSize] = useState<ContainerSize>('lg');
  const [dateSelected, setDateSelected] = useState(false);
  const [moveInDateNotice, setMoveInDateNotice] = useState('');
  const [bedroomSelected, setBedroomSelected] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const styleRef = useRef(false);
  const skipTourStep = useRef(false);

  const aptSync = useTourSchedulerData({
    source,
    javascriptId: useLeadsTourTimesEnabled ? null : javascriptId,
    selectedTourType,
    selectedDate,
  });

  const leads = useLeadsTourTimes({
    enabled: useLeadsTourTimesEnabled,
    propertyId: property?.multihub_property_id,
    selectedTourType,
    selectedDate,
  });

  const {
    tourTypes,
    availableDates,
    timeSlots,
    isInitializing,
    availabilityLoading,
    timeSlotsLoading,
  } = useLeadsTourTimesEnabled ? leads : aptSync;
  const credentials = useLeadsTourTimesEnabled ? null : aptSync.credentials;
  const leadRouter = useLeadsTourTimesEnabled ? leads.leadRouter : source.leadRouter;
  const refetchesOnTourTypeChange = useLeadsTourTimesEnabled || source.refetchesOnTourTypeChange;

  // Inject CSS once (animations only)
  useEffect(() => {
    if (styleRef.current) return;
    styleRef.current = true;
    const el = document.createElement('style');
    el.textContent = INJECTED_CSS;
    el.dataset.block = 'mh-tour-scheduler';
    document.head.appendChild(el);
    return () => { el.remove(); styleRef.current = false; };
  }, []);

  // ResizeObserver for container-based responsive
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 600;
      if (width < BREAKPOINTS.md) setContainerSize('sm');
      else if (width < BREAKPOINTS.lg) setContainerSize('md');
      else if (width < BREAKPOINTS.xl) setContainerSize('lg');
      else setContainerSize('xl');
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const tomorrowDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Center the success message on mobile when there's no redirect to take over.
  useEffect(() => {
    if (step !== 'success' || thankYouUrl) return;
    if (!window.matchMedia('(max-width: 480px)').matches) return;

    const frame = window.requestAnimationFrame(() => {
      wrapperRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    return () => { window.cancelAnimationFrame(frame); };
  }, [step, thankYouUrl]);

  // Build tour-option cards from adapter-supplied tour types + content tokens
  const tourOptions = useMemo<TourOption[]>(() => {
    const options: TourOption[] = [];
    const disableMap: Record<string, string | null> = {
      guided_tour: disableGuidedTour,
      self_guided_tour: disableSelfGuidedTour,
      live_video_tour: disableLiveVideoTour,
    };

    tourTypes.forEach((key) => {
      if (disableMap[key] === 'true') return;
      if (key === 'self_guided_tour' && selfGuidedTourLink) return;
      if (key === 'virtual_tour' && virtualTourLink) return;
      const def = TOUR_TYPE_DEFINITIONS[key];
      if (!def) return;
      const override = useLeadsTourTimesEnabled ? undefined : source.tourTypeOverrides?.[key];
      options.push({
        key,
        heading: override?.heading ?? def.heading,
        description: override?.description ?? def.description,
      });
    });

    if (selfGuidedTourLink && disableSelfGuidedTour !== 'true') {
      const def = TOUR_TYPE_DEFINITIONS.self_guided_tour;
      options.push({ key: 'self_guided_tour', heading: def.heading, description: def.description, link: { href: selfGuidedTourLink, target: '_self' } });
    }
    if (virtualTourLink) {
      const def = TOUR_TYPE_DEFINITIONS.virtual_tour;
      options.push({ key: 'virtual_tour', heading: def.heading, description: def.description, link: { href: virtualTourLink, target: '_blank' } });
    }
    return options;
  }, [tourTypes, source, useLeadsTourTimesEnabled, disableGuidedTour, disableSelfGuidedTour, disableLiveVideoTour, selfGuidedTourLink, virtualTourLink]);

  // Step + skip-state management; seeds selectedTourType to the first available option.
  useEffect(() => {
    if (isInitializing) {
      setStep('loading');
      return;
    }

    if (tourOptions.length === 0) {
      skipTourStep.current = false;
      setStep((prev) => (prev === 'loading' || prev === 'tour-type' ? 'tour-type' : prev));
      return;
    }

    setSelectedTourType((prev) => prev ?? tourOptions[0].key);

    const schedulable = tourOptions.filter((o) => !o.link);
    const shouldSkip = schedulable.length === 1 && tourOptions.length === 1;
    skipTourStep.current = shouldSkip;
    setStep((prev) => {
      if (prev !== 'loading' && prev !== 'tour-type') return prev;
      return shouldSkip ? 'date-time' : 'tour-type';
    });
  }, [isInitializing, tourOptions]);

  // Pick a sane default date when the dates list refreshes and nothing is selected
  useEffect(() => {
    if (selectedDate) return;
    const iso = firstAvailableIso(availableDates);
    if (iso) setSelectedDate(iso);
  }, [availableDates, selectedDate]);

  // Tour-type change clears the time selection (slots may differ per type);
  // for sources that refetch dates per tour type, also clear the date selection.
  useEffect(() => {
    setSelectedTime(null);
    if (refetchesOnTourTypeChange) setSelectedDate(null);
  }, [refetchesOnTourTypeChange, selectedTourType]);
  const bedroomOptions = useMemo(() => {
    if (bedroomListType === 'derived') {
      const fromFp = bedroomOptionsFromFloorplans(property?.floorplans);
      if (fromFp) return fromFp;
      return parseBedroomsRange(property?.property_bedrooms_range);
    }
    return DEFAULT_BEDROOMS;
  }, [bedroomListType, property?.floorplans, property?.property_bedrooms_range]);

  // Navigation handlers
  const goToStep = useCallback((target: WizardStep) => {
    setStep(target);
    setStatusMessage('');
  }, []);

  const handleContinue = useCallback(() => {
    if (step === 'tour-type') {
      if (!selectedTourType) return;
      const option = tourOptions.find((o) => o.key === selectedTourType);
      if (option?.link) {
        // External tour link — emit a canonical CTA event before navigating
        // so analytics sinks see the click. Self-guided / virtual tours have
        // dedicated catalog entries.
        try {
          if (selectedTourType === 'self_guided_tour') {
            emit(Event.self_guided_tour_clicked, {
              sectionId,
              blockId: 'multihub-tour-scheduler',
              href: option.link.href,
              target: option.link.target,
              linkText: option.heading,
            });
          } else if (selectedTourType === 'virtual_tour') {
            emit(Event.virtual_tour_clicked, {
              sectionId,
              blockId: 'multihub-tour-scheduler',
              href: option.link.href,
              target: option.link.target,
              linkText: option.heading,
            });
          }
        } catch {
          // Bus errors must never block navigation.
        }

        if (option.link.target === '_blank') {
          window.open(option.link.href, '_blank');
        } else {
          window.location.href = option.link.href;
        }
        return;
      }
      goToStep('date-time');
    } else if (step === 'date-time') {
      if (!selectedDate || !selectedTime) {
        setStatusMessage(!selectedDate ? 'Please select a date' : 'Please select a time');
        return;
      }
      goToStep('form');
    }
  }, [step, selectedTourType, selectedDate, selectedTime, tourOptions, goToStep, emit, sectionId]);

  const handleBack = useCallback(() => {
    if (step === 'form') {
      goToStep('date-time');
    } else if (step === 'date-time') {
      if (!skipTourStep.current) goToStep('tour-type');
    }
  }, [step, goToStep]);

  const handleMonthChange = useCallback((delta: number) => {
    setCurrentMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + delta);
      return next;
    });
  }, []);

  const handleDateSelect = useCallback((iso: string) => {
    setSelectedDate(iso);
    setSelectedTime(null);
  }, []);

  // Form submission
  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatusMessage('');

    const formData = new FormData(e.currentTarget);
    const data: Record<string, string> = {};
    formData.forEach((v, k) => { data[k] = typeof v === 'string' ? v.trim() : ''; });

    const errors: Record<string, string> = {};
    if (!data.first_name) errors.first_name = 'Required';
    if (!data.last_name) errors.last_name = 'Required';
    if (!data.email) errors.email = 'Required';
    else if (!validateEmail(data.email)) errors.email = 'e.g. jondoe@gmail.com';
    if (phoneRequired && !data.phone) errors.phone = 'Required';
    else if (data.phone && !validatePhone(data.phone)) errors.phone = 'Please enter a 10-digit phone number';
    if (!data.move_in_date) errors.move_in_date = 'Required';
    else {
      const sel = new Date(data.move_in_date);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      if (sel < now) errors.move_in_date = 'Cannot be in the past';
    }
    if (!data.apartment_size) errors.apartment_size = 'Required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setStatusMessage('Please address the errors above');
      return;
    }

    if (!window.__mh_leads__?.submitAsync) {
      setStatusMessage('This form was misconfigured. Please try again later.');
      setFormStatus('error');
      return;
    }

    setFormStatus('submitting');
    setFormErrors({});

    const payload: Record<string, unknown> = {
      PropertyId: credentials?.propertyId || '',
      FirstName: data.first_name,
      LastName: data.last_name,
      Email: data.email,
      Phone: data.phone,
      ApptDate: selectedDate || '',
      ApptTime: selectedTime || '',
      tourType: selectedTourType || '',
      DesiredBedrooms: data.apartment_size,
      DesiredMoveinDate: data.move_in_date,
    };

    const additionalData = {
      widgetSrc: 'schedule_tour_native',
      router: leadRouter,
      widget: 'MULTIHUB_TOUR_SCHEDULER',
      lead_type: 'SCHEDULE_TOUR',
      router_endpoint: null,
    };

    try {
      const response = await window.__mh_leads__!.submitAsync(payload, additionalData);
      if (response?.success === true) {
        setStep('success');
        setFormStatus('idle');

        // Emit canonical event so tracking sinks (GA4, Facebook, GTM, ...)
        // record the booking. Sensitive lead PII is deliberately omitted.
        try {
          emit(Event.tour_scheduled, {
            sectionId,
            blockId: 'multihub-tour-scheduler',
            tourType: selectedTourType ?? undefined,
            appointmentDate: selectedDate ?? undefined,
            appointmentTime: selectedTime ?? undefined,
          });
        } catch {
          // Bus errors must never break form UX.
          console.log('MultihubTourScheduler: error emitting tour_scheduled event');
        }

        if (!thankYouUrl) return;
        setTimeout(() => {
          window.location.href = thankYouUrl;
        }, 2500);
        return;
      } else {
        throw new Error('Lead submission failed');
      }
    } catch {
      console.error('[MultihubTourScheduler] Submission error');
      setFormStatus('error');
      setStatusMessage(
        errorMessage || 'There was an error scheduling your tour. If you have an adblocker, please disable it and try again.',
      );
    }
  }, [
    credentials,
    selectedDate,
    selectedTime,
    selectedTourType,
    leadRouter,
    thankYouUrl,
    errorMessage,
    phoneRequired,
    emit,
    sectionId,
  ]);

  const clearFieldError = useCallback((name: string) => {
    setFormErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const isCompact = containerSize === 'sm' || containerSize === 'md';
  const hasSchedulableOptions = tourOptions.some((o) => !o.link);

  const displayHeader = step === 'tour-type' || step === 'loading'
    ? headerText
    : `Schedule Your ${TOUR_TYPE_DEFINITIONS[selectedTourType || '']?.heading || 'Tour'}`;

  const isLastStep = step === 'form';
  const isFirstStep = step === 'tour-type' || (step === 'date-time' && skipTourStep.current);
  const isSubmitting = formStatus === 'submitting';
  const continueDisabled = (step === 'date-time' && (!selectedDate || !selectedTime || availabilityLoading)) || isSubmitting;

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'bg-background rounded-lg p-8 max-[480px]:p-5 shadow-xl w-full max-w-2xl mx-auto font-body box-border relative overflow-hidden',
        step === 'success' && 'min-h-[400px]',
      )}
    >
      {/* Header */}
      <EditableBlockToken
        localName="header_text"
        label="Header"
        defaultValue="Schedule a Tour"
        as="h3"
        className="text-2xl font-semibold font-heading text-black mb-6 mt-0 text-center"
      >
        {displayHeader}
      </EditableBlockToken>

      {/* Loading */}
      {step === 'loading' && <LoadingView />}

      {/* Tour Type Selection — empty state */}
      {step === 'tour-type' && tourOptions.length === 0 && (
        <ContactUsView contactUrl={contactUsUrl || '/contact-us'} />
      )}

      {/* Tour Type Selection — cards */}
      {step === 'tour-type' && tourOptions.length > 0 && (
        <div className="flex flex-col gap-3 mb-4">
          {tourOptions.map((option) => {
            const isActive = option.key === selectedTourType;
            return (
              <div
                key={option.key}
                role="button"
                tabIndex={0}
                aria-pressed={isActive}
                className={cn(
                  'flex items-center gap-4 px-5 py-4 border-2 border-border rounded-md cursor-pointer',
                  'transition-colors duration-200 bg-background',
                  isActive && 'border-primary bg-primary/5',
                )}
                onClick={() => setSelectedTourType(option.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedTourType(option.key);
                  }
                }}
              >
                <div className="shrink-0 text-primary">
                  <TourTypeIcon tourKey={option.key} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="m-0 text-base font-semibold text-foreground">{option.heading}</h4>
                  <p className="mt-1 mb-0 text-[0.8125rem] text-muted-foreground">{option.description}</p>
                </div>
                <div className={cn(
                  'shrink-0 text-primary transition-opacity duration-200',
                  isActive ? 'opacity-100' : 'opacity-0',
                )}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
              </div>
            );
          })}
          {!hasSchedulableOptions && (
            <p className="text-[0.8125rem] text-muted-foreground text-center mt-2">
              Not seeing the option you're looking for?{' '}
              <a href={contactUsUrl || '/contact-us'}>Contact us</a>
            </p>
          )}
        </div>
      )}

      {/* Date & Time Selection */}
      {step === 'date-time' && (
        <div className={cn('grid gap-6', isCompact ? 'grid-cols-1' : 'grid-cols-2')}>
          <div>
            <Calendar
              currentMonth={currentMonth}
              onMonthChange={handleMonthChange}
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              availableDates={availableDates}
              isLoading={availabilityLoading}
            />
          </div>
          <div className="flex flex-col">
            <p className="text-sm font-semibold text-foreground mb-2">
              {availabilityLoading
                ? 'Loading available dates...'
                : selectedDate
                  ? `Times for ${formatDisplayDate(selectedDate)}`
                  : 'Select a date'}
            </p>
            {timeSlotsLoading && selectedDate && !availabilityLoading && (
              <div className="flex items-center justify-center py-4 gap-2">
                <div
                  className="w-4 h-4 border-2 border-border border-t-primary rounded-full"
                  style={{ animation: 'mh-tour-spin 0.8s linear infinite' }}
                />
                <span className="text-sm text-muted-foreground">Loading times...</span>
              </div>
            )}
            {!availabilityLoading && !timeSlotsLoading && timeSlots.length === 0 && selectedDate && (
              <p className="text-sm text-muted-foreground text-center py-4 m-0">
                No available times for this date
              </p>
            )}
            {!timeSlotsLoading && timeSlots.length > 0 && !isCompact && (
              <div className={cn(
                'grid gap-2 max-h-60 overflow-y-auto pr-1',
                containerSize === 'lg' ? 'grid-cols-2' : 'grid-cols-1',
              )}>
                {timeSlots.map((time) => (
                  <button
                    key={time}
                    type="button"
                    className={cn(
                      'py-2 px-3 text-sm border border-border rounded-md cursor-pointer',
                      'bg-background text-foreground font-body text-center transition-colors duration-150',
                      time !== selectedTime && 'hover:bg-secondary hover:text-secondary-foreground hover:border-secondary',
                      time === selectedTime && 'border-primary bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground font-semibold',
                    )}
                    onClick={() => setSelectedTime(time)}
                  >
                    {time}
                  </button>
                ))}
              </div>
            )}
            {!timeSlotsLoading && timeSlots.length > 0 && isCompact && (
              <select
                className="w-full py-3.5 px-4 text-base border border-border rounded-md font-body bg-background cursor-pointer box-border text-foreground"
                value={selectedTime || ''}
                onChange={(e) => setSelectedTime(e.target.value || null)}
                aria-label="Select a time"
              >
                <option value="">Select a time</option>
                {timeSlots.map((time) => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {/* User Info Form */}
      {step === 'form' && (
        <>
          {/* Summary */}
          <div className="bg-primary/5 rounded-md px-4 py-3 mb-2 text-sm text-foreground leading-relaxed">
            <strong>{TOUR_TYPE_DEFINITIONS[selectedTourType || '']?.heading || 'Tour'}</strong>
            {' — '}
            {selectedDate && formatDisplayDate(selectedDate)} at {selectedTime}
          </div>

          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
            aria-label="Tour Scheduling Form"
            noValidate
          >
            {/* Name */}
            <div className="grid grid-cols-2 gap-4 max-[480px]:grid-cols-1">
              <div>
                <input
                  type="text"
                  name="first_name"
                  placeholder="First Name"
                  required
                  aria-required="true"
                  autoComplete="given-name"
                  disabled={isSubmitting}
                  onChange={() => clearFieldError('first_name')}
                  className={cn(
                    'w-full py-3.5 px-4 text-base border border-border rounded-md font-body',
                    'outline-none transition-colors duration-200 box-border text-gray-900 bg-background h-[3.25rem]',
                    formErrors.first_name && 'border-destructive',
                  )}
                />
                {formErrors.first_name && <p className="text-xs text-destructive mt-1 mb-0">{formErrors.first_name}</p>}
              </div>
              <div>
                <input
                  type="text"
                  name="last_name"
                  placeholder="Last Name"
                  required
                  aria-required="true"
                  autoComplete="family-name"
                  disabled={isSubmitting}
                  onChange={() => clearFieldError('last_name')}
                  className={cn(
                    'w-full py-3.5 px-4 text-base border border-border rounded-md font-body',
                    'outline-none transition-colors duration-200 box-border text-gray-900 bg-background h-[3.25rem]',
                    formErrors.last_name && 'border-destructive',
                  )}
                />
                {formErrors.last_name && <p className="text-xs text-destructive mt-1 mb-0">{formErrors.last_name}</p>}
              </div>
            </div>

            {/* Email */}
            <div>
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                required
                aria-required="true"
                autoComplete="email"
                disabled={isSubmitting}
                onChange={() => clearFieldError('email')}
                className={cn(
                  'w-full py-3.5 px-4 text-base border border-border rounded-md font-body',
                  'outline-none transition-colors duration-200 box-border text-gray-900 bg-background h-[3.25rem]',
                  formErrors.email && 'border-destructive',
                )}
              />
              {formErrors.email && <p className="text-xs text-destructive mt-1 mb-0">{formErrors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <PhoneInput
                name="phone"
                placeholder="Phone Number"
                required={phoneRequired}
                aria-required={phoneRequired ? 'true' : undefined}
                disabled={isSubmitting}
                onValueChange={() => clearFieldError('phone')}
                className={cn(
                  'w-full py-3.5 px-4 text-base border border-border rounded-md font-body',
                  'outline-none transition-colors duration-200 box-border text-gray-900 bg-background h-[3.25rem]',
                  formErrors.phone && 'border-destructive',
                )}
              />
              {formErrors.phone && <p className="text-xs text-destructive mt-1 mb-0">{formErrors.phone}</p>}
            </div>

            {/* Move-in Date & Apartment Size */}
            <div className="grid grid-cols-2 gap-4 max-[480px]:grid-cols-1">
              <div>
                <div
                  className="relative cursor-pointer overflow-hidden min-w-0 max-w-full"
                  onClick={() => {
                    const input = formRef.current?.querySelector<HTMLInputElement>('input[name="move_in_date"]');
                    try { input?.showPicker(); } catch { input?.focus(); }
                  }}
                >
                  <input
                    type="date"
                    name="move_in_date"
                    min={tomorrowDate}
                    required
                    aria-required="true"
                    disabled={isSubmitting}
                    onChange={(e) => {
                      // iOS WebKit ignores min/max in the picker UI (WebKit bug 225639),
                      // so clamp any past date back to the earliest allowed date.
                      if (e.currentTarget.value && e.currentTarget.value < tomorrowDate) {
                        e.currentTarget.value = tomorrowDate;
                        setMoveInDateNotice("Past dates aren't available.");
                      } else {
                        setMoveInDateNotice('');
                      }
                      setDateSelected(!!e.currentTarget.value);
                      clearFieldError('move_in_date');
                    }}
                    className={cn(
                      'w-full min-w-0 max-w-full py-3.5 pl-4 pr-11 text-base text-left border border-border rounded-md font-body',
                      'outline-none transition-colors duration-200 box-border bg-background h-[3.25rem]',
                      formErrors.move_in_date && 'border-destructive',
                      dateSelected ? 'text-gray-900' : 'text-transparent',
                    )}
                    style={!dateSelected ? { WebkitTextFillColor: 'transparent' } : undefined}
                    aria-label="Desired Move-in Date"
                  />
                  {!dateSelected && (
                    <span
                      className="absolute left-4 right-4 top-0 bottom-0 flex items-center text-gray-400 text-base font-body pointer-events-none overflow-hidden whitespace-nowrap text-ellipsis pr-7"
                      aria-hidden="true"
                    >
                      Desired Move-in Date
                    </span>
                  )}
                  <svg viewBox="0 0 20 20" fill="none" className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                {formErrors.move_in_date && <p className="text-xs text-destructive mt-1 mb-0">{formErrors.move_in_date}</p>}
                {moveInDateNotice && <p className="text-xs text-muted-foreground mt-1 mb-0">{moveInDateNotice}</p>}
              </div>
              <div>
                <div className="relative min-w-0 max-w-full">
                  <select
                    name="apartment_size"
                    required
                    aria-required="true"
                    disabled={isSubmitting}
                    defaultValue=""
                    onChange={(e) => {
                      clearFieldError('apartment_size');
                      setBedroomSelected(!!e.currentTarget.value);
                    }}
                    className={cn(
                      'w-full min-w-0 max-w-full py-3.5 pl-4 pr-11 text-base border border-border rounded-md font-body',
                      'bg-background cursor-pointer box-border h-[3.25rem] appearance-none',
                      formErrors.apartment_size && 'border-destructive',
                      bedroomSelected ? 'text-gray-900' : 'text-gray-400',
                    )}
                    aria-label="Desired Bedrooms"
                  >
                    <option value="" disabled>Bedrooms</option>
                    {bedroomOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <svg viewBox="0 0 20 20" fill="none" className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                {formErrors.apartment_size && <p className="text-xs text-destructive mt-1 mb-0">{formErrors.apartment_size}</p>}
              </div>
            </div>

            {/* Status */}
            {statusMessage && (
              <p className="text-sm text-destructive text-center m-0" role="alert" aria-live="polite">
                {statusMessage}
              </p>
            )}

            {/* Hidden submit for form.requestSubmit() */}
            <button type="submit" className="hidden" tabIndex={-1} />
          </form>
        </>
      )}

      {/* Success */}
      {step === 'success' && <SuccessView message={successMessage || 'Thank you! Your tour has been scheduled.'} />}

      {/* Navigation */}
      {step !== 'loading' && step !== 'success' && tourOptions.length > 0 && (
        <div className="flex justify-between items-center mt-6 gap-4">
          {!isFirstStep ? (
            <button
              type="button"
              className="py-3 px-4 text-[0.9375rem] font-semibold border-none rounded-md cursor-pointer font-body transition-colors duration-200 bg-transparent text-muted-foreground"
              onClick={handleBack}
            >
              Back
            </button>
          ) : (
            <div />
          )}
          <button
            type="button"
            className={cn(
              'py-3 px-6 text-[0.9375rem] font-semibold border-none rounded-md cursor-pointer font-body',
              'transition-colors duration-200 bg-primary text-primary-foreground ml-auto',
              continueDisabled && 'opacity-50 cursor-not-allowed',
            )}
            disabled={continueDisabled}
            onClick={() => {
              if (isLastStep) {
                formRef.current?.requestSubmit();
              } else {
                handleContinue();
              }
            }}
          >
            {isSubmitting ? 'Submitting...' : isLastStep ? (submitButtonText || 'Schedule Tour') : 'Continue'}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

const contentSchema: ComponentContentSchema = {
  slot: 'YardiTourScheduler',
  displayName: 'Tour Scheduler',
  tokens: {
    source: {
      label: 'Data Source',
      type: 'text',
      defaultValue: 'yardi',
      description: 'ApartmentSync PMS adapter: "yardi" (Yardi/RentCafe) or "entrata". Ignored when Use Leads Tour Times is enabled.',
    },
    use_leads_tour_times: {
      label: 'Use Leads Tour Times (Beta)',
      type: 'text',
      defaultValue: 'false',
      description: 'Beta. Do not change this unless you know what you are doing. When enabled, loads availability from the Multihub /tour-times endpoint instead of ApartmentSync. Leave off unless testing the new path. Lead submission still uses lead capture.',
    },
    header_text: {
      label: 'Header Text',
      type: 'text',
      defaultValue: 'Schedule a Tour',
      description: 'Main heading above the scheduler.',
    },
    success_message: {
      label: 'Success Message',
      type: 'text',
      defaultValue: 'Thank you! Your tour has been scheduled.',
      description: 'Message shown after successful submission.',
    },
    error_message: {
      label: 'Error Message',
      type: 'text',
      defaultValue: 'Something went wrong. Please try again.',
      description: 'Message shown on submission failure.',
    },
    submit_button_text: {
      label: 'Submit Button Text',
      type: 'text',
      defaultValue: 'Schedule Tour',
      description: 'Text displayed on the final submit button.',
    },
    contact_us_url: {
      label: 'Contact Us URL',
      type: 'text',
      defaultValue: '/contact-us',
      description: 'Fallback link when no tour options are available.',
    },
    javascript_id: {
      label: 'ApartmentSync JavaScript ID',
      type: 'text',
      defaultField: 'custom_apartmentsync_javascript_id',
      suggestedFields: ['custom_apartmentsync_javascript_id'],
      description: 'Encoded token for property credentials lookup. Usually auto-populated from property data. Unused when Use Leads Tour Times is enabled.',
      required: true,
    },
    disable_guided_tour: {
      label: 'Disable Guided Tour',
      type: 'text',
      defaultValue: 'false',
      description: 'Set to "true" to hide the guided tour option.',
    },
    disable_self_guided_tour: {
      label: 'Disable Self-Guided Tour',
      type: 'text',
      defaultValue: 'false',
      description: 'Set to "true" to hide the self-guided tour option.',
    },
    disable_live_video_tour: {
      label: 'Disable Live Video Tour',
      type: 'text',
      defaultValue: 'false',
      description: 'Set to "true" to hide the live video tour option (Yardi only; ignored for Entrata).',
    },
    self_guided_tour_link: {
      label: 'Self-Guided Tour Link (external)',
      type: 'text',
      defaultValue: '',
      description: 'External URL for self-guided tours. Required path for Entrata; on Yardi, hides the API self-guided option when set.',
    },
    virtual_tour_link: {
      label: 'Virtual Tour Link',
      type: 'text',
      defaultValue: '',
      description: 'External URL for virtual tours. Virtual tours are always link-based.',
    },
    thank_you_url: {
      label: 'Thank You Redirect URL',
      type: 'url',
      defaultValue: '',
      description: 'If set, redirects to this URL after successful submission.',
    },
    bedroom_list_type: {
      label: 'Bedroom List Type',
      type: 'text',
      defaultValue: 'derived',
      description: 'How to populate bedroom options: "derived" (from property floorplans/bedrooms_range) or "default" (static list).',
    },
    phone_required: {
      label: 'Require Phone Number',
      type: 'text',
      defaultValue: 'true',
      description: 'Set to "false" to make the phone number field optional.',
    },
  },
};

// ============================================================================
// Design Schema
// ============================================================================

const designSchema: ComponentDesignSchema = {
  slot: 'YardiTourScheduler',
  displayName: 'Tour Scheduler',
  tokens: {
    'color.primary': {
      label: 'Primary Color',
      type: 'color',
      cssVariable: '--vibe-color-primary',
      description: 'Main accent color for buttons, selected states, and highlights.',
    },
    'color.background': {
      label: 'Background',
      type: 'color',
      cssVariable: '--vibe-color-background',
      description: 'Form card background color.',
    },
    'color.text': {
      label: 'Text Color',
      type: 'color',
      cssVariable: '--vibe-color-text',
      description: 'Primary text color.',
    },
    'color.textMuted': {
      label: 'Muted Text',
      type: 'color',
      cssVariable: '--vibe-color-text-muted',
      description: 'Secondary/muted text color.',
    },
    'color.textOnPrimary': {
      label: 'Text on Primary',
      type: 'color',
      cssVariable: '--vibe-color-text-on-primary',
      description: 'Text color on primary-colored backgrounds (buttons, selected dates).',
    },
    'color.border': {
      label: 'Border Color',
      type: 'color',
      cssVariable: '--vibe-color-border',
      description: 'Border color for inputs, cards, and calendar cells.',
    },
    'color.error': {
      label: 'Error Color',
      type: 'color',
      cssVariable: '--vibe-color-error',
      description: 'Color for validation errors.',
    },
    'font.heading': {
      label: 'Heading Font',
      type: 'font',
      cssVariable: '--vibe-font-heading',
      description: 'Font family for the form header.',
    },
    'font.body': {
      label: 'Body Font',
      type: 'font',
      cssVariable: '--vibe-font-body',
      description: 'Font family for form fields, labels, and body text.',
    },
  },
};

// ============================================================================
// Block Definition
// ============================================================================

export const multihubTourSchedulerBlock: BlockDefinition = {
  id: 'multihub-tour-scheduler',
  name: 'Multihub Tour Scheduler',
  description:
    'Multi-step tour scheduling wizard. Fetches available slots (ApartmentSync by default, or GET /tour-times when enabled) and submits leads.',
  category: 'Forms',
  placement: 'section',
  defaultScope: 'page',
  component: MultihubTourSchedulerComponent,
  contentSchema,
  designSchema,
  requirements: {
    designTokens: [
      'color.primary',
      'color.background',
      'color.border',
      'color.text',
      'color.textMuted',
      'color.textOnPrimary',
      'color.error',
      'font.heading',
      'font.body',
      'radius.md',
      'radius.lg',
    ],
    dataTokens: [],
  },
  tags: ['tour', 'schedule', 'yardi', 'rentcafe', 'entrata', 'appointment', 'booking', 'multihub'],
  defaultGridSpan: 12,
};
